import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ClientLayout from "@/components/ClientLayout";
import api from "@/lib/api";
import { ProvenanceBadge } from "@/components/Badge";
import HintBubble from "@/components/HintBubble";
import { Loader2, Sparkles, ArrowLeft, Flag, HeartPulse, Gauge, Mountain, Lock, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function SessionRun() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [askInput, setAskInput] = useState("");
  const [asking, setAsking] = useState(false);
  const [maiReply, setMaiReply] = useState(null);
  const [timing, setTiming] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [phase, setPhase] = useState("run"); // run | feedback
  const [fb, setFb] = useState({ completion: "terminée", difficulty: 3, fatigue: 3, rpe: 5, pain: "aucune", pain_zone: "", pain_intensity: 3, comment: "", continuous_run_min: null, continuous_unknown: false });
  const [saving, setSaving] = useState(false);
  const [adaptInfo, setAdaptInfo] = useState(null); // {explanation, reason} après feedback si N+1 adaptée
  const [showWhy, setShowWhy] = useState(false);

  const load = async () => {
    const r = await api.get("/sessions");
    setSession(r.data.find((s) => s.session_id === id) || null);
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    // Réponse déterministe "Quand faire cette séance ?" (aucun appel Claude).
    api.get(`/sessions/${id}/timing`).then((r) => setTiming(r.data)).catch(() => setTiming(null));
  }, [id]);

  const askMai = async (text) => {
    const q = (text ?? askInput).trim();
    if (!q) return;
    setAsking(true);
    try {
      // MÊME moteur MAI que le chat principal — seul le contexte séance est joint automatiquement.
      const r = await api.post("/chat/message", { content: q, session_id: id });
      setMaiReply(r.data);
      setAskInput("");
    } catch { toast.error("MAI est indisponible un instant, réessaie."); }
    finally { setAsking(false); }
  };

  const confirmAdaptation = async () => {
    if (!maiReply?.adaptation_id) return;
    setConfirming(true);
    try {
      const r = await api.post(`/chat/adaptation/${maiReply.adaptation_id}/confirm`);
      if (r.data.status === "applied") { toast.success("Programme modifié"); await load(); setMaiReply({ ...maiReply, status: "applied" }); }
      else { toast.message(r.data.message || "Programme inchangé"); }
    } catch { toast.error("Échec — ton programme n'a pas changé."); }
    finally { setConfirming(false); }
  };

  const submitFeedback = async () => {
    setSaving(true);
    try {
      const { continuous_unknown, ...rest } = fb;
      const payload = { session_id: id, ...rest,
        continuous_run_min: continuous_unknown ? null : (fb.continuous_run_min ?? null) };
      const r = await api.post("/sessions/feedback", payload);
      toast.success("Feedback enregistré");
      // ENTRE DEUX SÉANCES : si MAI a adapté la prochaine séance, on l'annonce simplement.
      const bs = r.data?.between_sessions;
      if (bs && bs.adapted && bs.explanation) {
        setAdaptInfo({ explanation: bs.explanation, reason: bs.reason || "" });
        setPhase("adapted");
      } else {
        navigate("/app");
      }
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  if (!session) return <ClientLayout><div className="pulse-organic w-3 h-3 rounded-full bg-sage mx-auto mt-20" /></ClientLayout>;

  if (session.locked) {
    return (
      <ClientLayout>
        <button onClick={() => navigate("/programme")} className="mt-4 inline-flex items-center gap-1.5 text-sm text-obsidian/60 hover:text-obsidian"><ArrowLeft size={16} /> Mon programme</button>
        <div className="fade-up mt-10 max-w-md mx-auto text-center" data-testid="session-locked">
          <span className="mx-auto w-14 h-14 rounded-full bg-secondary flex items-center justify-center"><Lock size={24} className="text-obsidian/50" /></span>
          <h1 className="mt-5 text-2xl sm:text-3xl font-light tracking-tight">Séance verrouillée</h1>
          <p className="mt-3 text-obsidian/60 text-sm sm:text-base">Cette séance fait partie d'un cycle non encore payé. Débloque le cycle suivant pour y accéder.</p>
          <button data-testid="session-locked-cta" onClick={() => navigate("/programme")}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-6 py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform">
            Passer au cycle suivant <ChevronRight size={16} />
          </button>
        </div>
      </ClientLayout>
    );
  }

  if (phase === "adapted") {
    return (
      <ClientLayout>
        <div className="fade-up max-w-md mx-auto text-center mt-10" data-testid="between-sessions-adapted">
          <span className="mx-auto w-14 h-14 rounded-full bg-sage/15 flex items-center justify-center">
            <Sparkles size={24} className="text-sage" />
          </span>
          <h1 className="mt-5 text-2xl sm:text-3xl font-light tracking-tight">MAI a ajusté ta prochaine séance</h1>
          <p className="mt-3 text-obsidian/60 text-sm sm:text-base" data-testid="between-sessions-message">{adaptInfo?.explanation}</p>
          {adaptInfo?.reason && (
            <div className="mt-5">
              <button data-testid="between-sessions-why-toggle" onClick={() => setShowWhy((v) => !v)}
                className="text-sm text-sage underline underline-offset-4">Pourquoi ?</button>
              {showWhy && (
                <p data-testid="between-sessions-why" className="mt-2.5 text-sm text-obsidian/55 bg-secondary rounded-xl px-4 py-3">
                  {adaptInfo.reason}
                </p>
              )}
            </div>
          )}
          <button data-testid="between-sessions-continue" onClick={() => navigate("/app")}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-6 py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform">
            Continuer <ChevronRight size={16} />
          </button>
        </div>
      </ClientLayout>
    );
  }

  if (phase === "feedback") {
    return (
      <ClientLayout>
        <div className="fade-up max-w-lg">
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight">Comment s'est passée la séance ?</h1>
          <p className="mt-2.5 text-obsidian/55 text-sm sm:text-base">Feedback rapide — l'IA en tire des données structurées.</p>

          <HintBubble
            hintKey="first_validation"
            className="mt-5"
            title="Ta première validation"
            body="Indique si la séance est terminée, partielle ou non réalisée, puis note ton RPE (intensité ressentie de 1 à 10) et toute gêne éventuelle. Ces retours ajustent ton entraînement."
          />

          <div className="mt-8">
            <Label>Séance</Label>
            <div className="grid grid-cols-3 gap-2.5 mt-2.5">
              {["terminée", "partielle", "non réalisée"].map((c) => (
                <button key={c} data-testid={`fb-completion-${c}`} onClick={() => setFb({ ...fb, completion: c })}
                  className={`min-h-[52px] rounded-xl border px-3 py-3 text-sm capitalize active:scale-[0.98] transition-[background-color,border-color,transform] ${fb.completion === c ? "border-obsidian bg-obsidian text-white" : "border-border bg-white"}`}>{c}</button>
              ))}
            </div>
          </div>

          <Scale label="Difficulté ressentie" value={fb.difficulty} onChange={(v) => setFb({ ...fb, difficulty: v })} testid="fb-difficulty" />
          <Scale label="Fatigue" value={fb.fatigue} onChange={(v) => setFb({ ...fb, fatigue: v })} testid="fb-fatigue" />

          <div className="mt-7">
            <div className="flex items-baseline justify-between">
              <Label>RPE — intensité ressentie (1-10)</Label>
              {session.rpe_cible != null && <span className="text-xs text-obsidian/45">cible ≈ {session.rpe_cible}</span>}
            </div>
            <p className="text-xs text-obsidian/45 mt-1.5">1-2 très facile · 3-4 facile · 5-6 modéré · 7-8 difficile · 9 très difficile · 10 maximal</p>
            <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mt-2.5">
              {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                <button key={n} data-testid={`fb-rpe-${n}`} onClick={() => setFb({ ...fb, rpe: n })}
                  className={`min-h-[48px] rounded-xl border py-2.5 text-base font-medium tabnum active:scale-[0.98] transition-[background-color,border-color,transform] ${fb.rpe === n ? "border-obsidian bg-obsidian text-white" : "border-border bg-white"}`}>{n}</button>
              ))}
            </div>
          </div>

          <div className="mt-7">
            <Label>Gêne / douleur</Label>
            <div className="grid grid-cols-3 gap-2.5 mt-2.5">
              {["aucune", "légère", "importante"].map((p) => (
                <button key={p} data-testid={`fb-pain-${p}`} onClick={() => setFb({ ...fb, pain: p })}
                  className={`min-h-[52px] rounded-xl border px-3 py-3 text-sm capitalize active:scale-[0.98] transition-[background-color,border-color,transform] ${fb.pain === p ? (p === "importante" ? "border-destructive bg-destructive text-white" : "border-obsidian bg-obsidian text-white") : "border-border bg-white"}`}>{p}</button>
              ))}
            </div>
            {fb.pain !== "aucune" && (
              <div className="mt-3.5 space-y-3">
                <input data-testid="fb-pain-zone" value={fb.pain_zone} onChange={(e) => setFb({ ...fb, pain_zone: e.target.value })}
                  placeholder="Zone (ex : mollet gauche, genou droit…)"
                  className="w-full rounded-xl border border-border bg-white px-4 py-3 text-base focus:outline-none focus:border-sage" />
                <div>
                  <div className="flex items-baseline justify-between"><span className="text-xs text-obsidian/50">Intensité de la douleur (0-10)</span><span className="text-sm font-semibold">{fb.pain_intensity}</span></div>
                  <input data-testid="fb-pain-intensity" type="range" min="0" max="10" value={fb.pain_intensity}
                    onChange={(e) => setFb({ ...fb, pain_intensity: parseInt(e.target.value) })} className="w-full mt-1 accent-destructive" />
                </div>
              </div>
            )}
          </div>


          {(session.family === "reprise" || session.focus === "reprise") && (
            <div className="mt-7" data-testid="fb-continuous-block">
              <Label>Quelle a été ta plus longue durée courue sans marcher pendant cette séance ?</Label>
              <p className="text-xs text-obsidian/45 mt-1.5">
                On parle de la plus longue portion COURUE d'affilée (pas du temps total). C'est ce qui permet de valider ta progression vers la course continue.
              </p>
              <div className="mt-2.5 flex items-center gap-3">
                <input data-testid="fb-continuous-min" type="number" min="0" max="180" inputMode="numeric"
                  disabled={fb.continuous_unknown}
                  value={fb.continuous_unknown ? "" : (fb.continuous_run_min ?? "")}
                  onChange={(e) => setFb({ ...fb, continuous_run_min: e.target.value === "" ? null : Math.max(0, parseInt(e.target.value) || 0) })}
                  placeholder={session.planned_continuous_min ? `ex : ${session.planned_continuous_min}` : "ex : 20"}
                  className="w-32 rounded-xl border border-border bg-white px-4 py-3 text-base focus:outline-none focus:border-sage disabled:opacity-50" />
                <span className="text-sm text-obsidian/50">minutes</span>
                <button type="button" data-testid="fb-continuous-unknown"
                  onClick={() => setFb({ ...fb, continuous_unknown: !fb.continuous_unknown, continuous_run_min: null })}
                  className={`ml-auto min-h-[44px] rounded-xl border px-4 py-2 text-sm active:scale-[0.98] transition-[background-color,border-color,transform] ${fb.continuous_unknown ? "border-obsidian bg-obsidian text-white" : "border-border bg-white"}`}>
                  Je ne sais pas
                </button>
              </div>
            </div>
          )}

          <div className="mt-7" data-testid="fb-realized-duration-block">
            <Label>Durée réellement réalisée (optionnel)</Label>
            <p className="text-xs text-obsidian/45 mt-1.5">Temps total réellement effectué. Laisse vide si tu ne sais pas : rien ne sera inventé.</p>
            <div className="mt-2.5 flex items-center gap-3">
              <input data-testid="fb-realized-duration-min" type="number" min="0" max="600" inputMode="numeric"
                value={fb.realized_duration_min ?? ""}
                onChange={(e) => setFb({ ...fb, realized_duration_min: e.target.value === "" ? null : Math.max(0, parseInt(e.target.value) || 0) })}
                placeholder={session.duration_min ? `prévu : ${session.duration_min}` : "ex : 45"}
                className="w-32 rounded-xl border border-border bg-white px-4 py-3 text-base focus:outline-none focus:border-sage" />
              <span className="text-sm text-obsidian/50">minutes</span>
            </div>
          </div>

          <div className="mt-7">
            <Label>Commentaire (optionnel)</Label>
            <textarea data-testid="fb-comment" value={fb.comment} onChange={(e) => setFb({ ...fb, comment: e.target.value })} rows={3}
              placeholder="Ex : très bonne séance mais les tractions étaient plus difficiles aujourd'hui."
              className="mt-2.5 w-full resize-none rounded-xl border border-border bg-white px-4 py-3 text-base focus:ring-1 focus:ring-obsidian focus:outline-none" />
          </div>

          <button data-testid="fb-submit" onClick={submitFeedback} disabled={saving}
            className="mt-8 w-full rounded-full bg-obsidian text-white py-5 text-base font-medium hover:-translate-y-0.5 active:scale-[0.99] transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {saving ? <Loader2 size={18} className="animate-spin" /> : null} Enregistrer
          </button>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-obsidian/50 hover:text-obsidian"><ArrowLeft size={15} /> Retour</button>
      <div className="fade-up mt-4">
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight">{session.title}</h1>
        <div className="mt-3 text-obsidian/55 text-sm sm:text-base">{session.duration_min} min · {session.exercises?.length} exercices{session.rpe_cible != null ? ` · RPE cible ≈ ${session.rpe_cible}/10` : ""}</div>
        {session.adapted && (
          <div className="mt-4">
            <ProvenanceBadge type="adaptation">SÉANCE ADAPTÉE PAR L'IA</ProvenanceBadge>
            {session.adaptation_note && <p className="mt-2 text-sm sm:text-base text-obsidian/60">{session.adaptation_note}</p>}
          </div>
        )}

        {/* Encart déterministe : Quand faire cette séance ? (aucun appel Claude) */}
        {timing?.answer && (
          <div className="mt-6 rounded-2xl border border-border bg-sage/5 p-4 sm:p-5" data-testid="timing-card">
            <div className="flex items-center gap-2 text-sage font-medium text-sm">
              <Gauge size={16} /> Quand faire cette séance ?
              {timing.order_in_week ? <span className="ml-auto text-xs text-obsidian/40" data-testid="order-in-week">Séance {timing.order_in_week} · {timing.load}</span> : null}
            </div>
            <p className="mt-1.5 text-sm sm:text-base text-obsidian/70">{timing.answer}</p>
            {timing.next_hard_message && (
              <p className="mt-2 text-xs sm:text-sm text-obsidian/45" data-testid="next-hard">{timing.next_hard_message}</p>
            )}
            <p className="mt-2 text-xs text-obsidian/40" data-testid="missed-guidance">{timing.missed_guidance}</p>
          </div>
        )}

        {/* Demander à MAI — même assistante que le chat principal, contexte séance auto-joint */}
        <div className="mt-6 rounded-2xl border border-border bg-white p-5 sm:p-6" data-testid="ask-mai-card">
          <Label>Parler de cette séance avec MAI</Label>
          <p className="text-xs sm:text-sm text-obsidian/45 mt-1.5">MAI peut tout t'expliquer et t'aider sur cette séance — pas seulement la modifier.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {["Je n'ai que 30 minutes", "Je suis à l'hôtel", "Comment choisir mon allure ?"].map((s) => (
              <button key={s} data-testid={`mai-suggestion-${s.slice(0, 8)}`} onClick={() => askMai(s)} disabled={asking}
                className="text-xs rounded-full border border-border px-3 py-1.5 text-obsidian/70 hover:bg-sage/10 disabled:opacity-40 transition-colors">{s}</button>
            ))}
          </div>
          <div className="mt-3.5 flex flex-col sm:flex-row gap-2.5">
            <input data-testid="ask-mai-input" value={askInput} onChange={(e) => setAskInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && askMai()}
              className="flex-1 rounded-xl border border-border px-4 py-3 text-base focus:ring-1 focus:ring-obsidian focus:outline-none" placeholder="Pose ta question à MAI…" />
            <button data-testid="ask-mai-button" onClick={() => askMai()} disabled={asking || !askInput.trim()}
              className="min-h-[48px] rounded-xl bg-sage/15 text-sage px-5 text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-40 active:scale-[0.98] transition-transform">
              {asking ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Demander à MAI
            </button>
          </div>
          {maiReply && (
            <div className="mt-4 text-sm sm:text-base" data-testid="mai-reply">
              {maiReply.status === "conseil" && <ProvenanceBadge type="recommandation" className="mb-2">CONSEIL — AUCUN CHANGEMENT DU PROGRAMME</ProvenanceBadge>}
              {maiReply.status === "proposed" && <ProvenanceBadge type="adaptation" className="mb-2">AJUSTEMENT PROPOSÉ — EN ATTENTE DE CONFIRMATION</ProvenanceBadge>}
              {maiReply.status === "applied" && <ProvenanceBadge type="adaptation" className="mb-2">PROGRAMME MODIFIÉ — ENREGISTRÉ</ProvenanceBadge>}
              {maiReply.status === "coach" && <ProvenanceBadge type="recommandation" className="mb-2">DEMANDE COACH — ANALYSE NÉCESSAIRE</ProvenanceBadge>}
              <p className="text-obsidian/75 whitespace-pre-line">{maiReply.response}</p>
              {Array.isArray(maiReply.proposed_changes) && maiReply.proposed_changes.length > 0 && (
                <ul className="mt-2.5 space-y-1 text-obsidian/70" data-testid="mai-proposed-changes">
                  {maiReply.proposed_changes.map((c, i) => <li key={i} className="flex gap-1.5"><ChevronRight size={15} className="mt-0.5 shrink-0 text-sage" />{c}</li>)}
                </ul>
              )}
              {maiReply.upgrade_hint && <p className="mt-2 text-obsidian/50 text-sm">{maiReply.upgrade_hint}</p>}
              {maiReply.coach_hint && <p className="mt-2 text-obsidian/50 text-sm">{maiReply.coach_hint}</p>}
              {maiReply.status === "proposed" && maiReply.adaptation_id && (
                <button data-testid="mai-confirm-button" onClick={confirmAdaptation} disabled={confirming}
                  className="mt-3 min-h-[44px] rounded-xl bg-obsidian text-white px-5 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-40 active:scale-[0.98] transition-transform">
                  {confirming ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Confirmer l'ajustement
                </button>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 space-y-3.5">
          {session.exercises?.map((e, i) => (
            <div key={i} className="rounded-2xl border border-border bg-white p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-4">
                <span className="text-base sm:text-lg font-medium inline-flex items-center gap-2">
                  {e.is_cote && <Mountain size={17} className="text-sage shrink-0" />}{e.name}
                </span>
                {e.detail
                  ? <span className="font-mono text-sm sm:text-base text-obsidian/70 sm:text-right leading-relaxed">{e.detail}</span>
                  : <span className="font-mono text-sm sm:text-base text-obsidian/60">{e.sets} × {e.reps}</span>}
              </div>

              {(e.pct_vma || e.fc_cible) && (
                <div className="mt-3.5 flex flex-wrap items-center gap-2">
                  {e.pct_vma ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary text-obsidian/70 text-xs sm:text-sm font-medium px-3 py-1.5 tabnum">{e.pct_vma}% VMA</span>
                  ) : null}
                  {e.fc_cible ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FBEDED] text-[#B4413C] text-xs sm:text-sm font-medium px-3 py-1.5">
                      <HeartPulse size={14} /> {e.fc_cible}
                    </span>
                  ) : null}
                </div>
              )}

              {e.ressenti && (
                <div className="mt-2.5 flex items-start gap-2 text-sm sm:text-base text-obsidian/70">
                  <Gauge size={16} className="text-sage shrink-0 mt-0.5" />
                  <span><span className="text-obsidian/45">Ressenti :</span> {e.ressenti}</span>
                </div>
              )}

              {!e.detail && (e.intensity || e.rest) && <div className="mt-2 text-xs sm:text-sm text-obsidian/50">{e.intensity}{e.rest ? ` · repos ${e.rest}` : ""}</div>}
              {e.reason && <div className="mt-3 text-xs sm:text-sm text-obsidian/50 border-l-2 border-sage/40 pl-3 leading-relaxed">{e.reason}</div>}
            </div>
          ))}
        </div>

        <button data-testid="finish-session-button" onClick={() => setPhase("feedback")}
          className="mt-8 w-full rounded-full bg-obsidian text-white py-5 text-base font-medium hover:-translate-y-0.5 active:scale-[0.99] transition-transform inline-flex items-center justify-center gap-2">
          <Flag size={18} /> Terminer la séance
        </button>
      </div>
    </ClientLayout>
  );
}

function Label({ children }) { return <div className="text-xs sm:text-sm tracking-[0.16em] uppercase font-semibold text-obsidian/50">{children}</div>; }

function Scale({ label, value, onChange, testid }) {
  return (
    <div className="mt-7">
      <Label>{label}</Label>
      <div className="grid grid-cols-5 gap-2.5 mt-2.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`${testid}-${n}`} onClick={() => onChange(n)}
            className={`min-h-[52px] rounded-xl border py-3 text-base font-medium tabnum active:scale-[0.98] transition-[background-color,border-color,transform] ${value === n ? "border-obsidian bg-obsidian text-white" : "border-border bg-white"}`}>{n}</button>
        ))}
      </div>
    </div>
  );
}
